<script setup lang="ts">
import { computed, defineProps, defineEmits } from 'vue';
import { useArticlesCustomisation } from '../composables/useArticlesCustomisation';
import { useArticleDetails } from '@/common/api/article.api';
import { stripHtml, truncateText } from '../utils/textUtils';
import router from '@/router';

interface Props {
  sectionKey: string;
  autofillId?: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'click', articleId: string): void;
}>();

const customisation = useArticlesCustomisation(props.sectionKey);

const containerStyle = computed(() => {
  const borderColor = customisation.borderColor.value;

  return {
    borderColor: borderColor || 'transparent',
  };
});

const textStyle = computed(() => {
  const color = customisation.textColor.value;
  return color ? { color } : {};
});

const subTextStyle = computed(() => {
  const color = customisation.textColor.value;
  return color ? { color } : {};
});

// Determine which article ID to use
const articleId = computed(() => {
  // If autofill is enabled, use autofillId from props
  const autofillValue = customisation.useAutofill.value;
  const isAutofillEnabled = autofillValue === true || (autofillValue as any) === 'true';

  if (isAutofillEnabled && props.autofillId) {
    return props.autofillId;
  }

  // Otherwise, use the article content from customisation
  return customisation.articleContent.value;
});

// TanStack Query hook for article details (uses mocked data in mocked flow, same as SelfServiceLink)
const { data: articleData, isLoading: isLoadingArticle, error: articleError } = useArticleDetails(articleId);

const articleTitle = computed(() => {
  if (articleError.value) return 'Error loading article';
  return articleData.value?.title ?? '';
});

const articleBody = computed(() => {
  if (!articleData.value?.body) return '';
  const strippedBody = stripHtml(articleData.value.body);
  return truncateText(strippedBody, 100);
});

const handleClick = () => {
  if (articleId.value) {
    emit('click', articleId.value);
    router.push(`/new-article/${articleId.value}`);
  }
};

const shouldRender = computed(() => {
  if (!customisation.isVisible.value) {
    return false;
  }

  // Show if we have an article ID to load
  return !!articleId.value;
});
</script>

<template>
  <div v-if="shouldRender" class="article-row card-radius shadow-soft" :style="containerStyle" @click="handleClick">
    <div class="article-content">
      <h3 class="article-title theme-font-main" :style="textStyle">
        <span v-if="isLoadingArticle">Loading...</span>
        <span v-else>{{ articleTitle }}</span>
      </h3>
      <p v-if="articleBody" class="article-text theme-font-subtext" :style="subTextStyle">
        {{ articleBody }}
      </p>
    </div>
    <span class="chevron-icon">›</span>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/scss/variable.scss';
@import '@/assets/scss/mixin.scss';

.article-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 16px;
  margin-bottom: 12px;
  cursor: pointer;
  border: 1px solid;
}

.article-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.article-title {
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  line-height: 1.5;
}

.article-text {
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
  opacity: 0.8;
}

.chevron-icon {
  font-size: 24px;
  font-weight: 300;
  flex-shrink: 0;
}
</style>
